/**
 * Which way in the browser used for its LAST COMPLETED sign-in, so the login
 * page can mark it. Deliberately local-only: nothing is read from the typed
 * email and nothing is asked of the server, so the page cannot be used to find
 * out which method an email address uses.
 */
export enum LastLoginMethod {
  Google = "google",
  Password = "password",
}

/**
 * The key survives `logoutCleanup`'s `localStorage.clear()` via the preserve
 * list in `clearClientAuthState` — the hint is needed on the NEXT sign-in, so
 * logging out must not wipe it. The name avoids every substring in that file's
 * `STORAGE_AUTH_KEY_PARTS` ("auth", "token", "session", "login", …) so a
 * pattern-based sweep would not take it either.
 */
export const LAST_SIGN_IN_METHOD_STORAGE_KEY = "ffair_last_sign_in_method";

const isLastLoginMethod = (value: unknown): value is LastLoginMethod =>
  (Object.values(LastLoginMethod) as unknown[]).includes(value);

/**
 * Reads the stored method. A missing or unreadable value (private window,
 * cleared site data, storage disabled) is simply "no hint" — the login page
 * then renders both ways in and marks neither.
 */
export const readLastLoginMethod = (): LastLoginMethod | null => {
  try {
    const value = localStorage.getItem(LAST_SIGN_IN_METHOD_STORAGE_KEY);

    return isLastLoginMethod(value) ? value : null;
  } catch {
    return null;
  }
};

/**
 * Called where a session actually starts — after the two-factor step for a user
 * who has one — so a failed sign-in or an abandoned challenge leaves the
 * previously stored method untouched.
 */
export const writeLastLoginMethod = (method: LastLoginMethod): void => {
  try {
    localStorage.setItem(LAST_SIGN_IN_METHOD_STORAGE_KEY, method);
  } catch {
    // Storage is a convenience here; losing it only costs the hint.
  }
};
